import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { debounceTime, distinctUntilChanged, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { EpsgIndex } from '../../../../shared/models/epsgData';
import epsgIndexData from 'epsg-index/all.json'; // Import the list of all EPSG codes
import { ControlPoint } from '../../../../shared/models/controlPoint';
import { Site } from '../../../../shared/models/site';
import { ControlPointsService } from '../../services/control-points-service';
import { CurrentStateService } from '../../../../core/services/current-state.service';
import { FonctionsService } from '../../../../shared/services/utilities/fonctions.service';
import { PopupCloseMode } from '../../models/popupMode';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Seismograph } from '../../../../shared/models/seismograph';
import { SeismographService } from '../../../seismograph-devices/services/seismograph-service';
import { CustomCoordinate } from '../../../../shared/models/customCoordinate';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DraggableDirective } from '../../../../shared/directive/draggable';
import { PonderationService } from '@shared/services/ponderation/ponderation.service';
import { Standard } from '@shared/models/standard';

const EPSG_REFERENCE_OL: string = 'EPSG:3857';

interface MyFormData {
  number: number;
  name: string;
  description: string;
  device: string;
  x: number;
  y: number;
  z: number;
}
interface SelectedEpsg {
  id: number;
  text: string;
}

@Component({
  selector: 'app-popup-create-control-point',
  imports: [
    CommonModule,
    NgSelectComponent,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    DraggableDirective
  ],
  templateUrl: './control-point-modale.component.html',
  styleUrl: './control-point-modale.component.scss'
})

export class ControlPointModaleComponent implements OnInit, OnDestroy {
  @ViewChild('popup') popup!: ElementRef;
  @Output() close = new EventEmitter<PopupCloseMode>();

  protected site: Site | undefined;
  protected lastNumber: number | undefined = undefined;
  protected position_EPSG_REFERENCE_OL?: CustomCoordinate;
  protected positionEpsgSite?: CustomCoordinate;

  protected filteredEpsgCodes: any[] = []; // Holds filtered EPSG codes to show in the dropdown
  protected typeahead$ = new Subject<string>(); // Subject to capture user input in real-time
  protected epsgCodes: any[] = [];
  protected selectedEpsg?: SelectedEpsg;
  protected epsgIndex: EpsgIndex = {};

  protected form: FormGroup | undefined;

  protected loading = false;
  protected epsgIsOk: boolean | undefined = undefined;

  protected seismographList: Seismograph[] = [];
  protected selectedSeismographId: number | undefined = undefined;;

  private unsubscribe$ = new Subject<void>();

  protected isEdit: boolean = false;

  protected isDegre: boolean = false;

  protected C1: string = 'X';
  protected C2: string = 'Y';
  protected C3: string = 'Z';

  protected PopupCloseMode = PopupCloseMode;

  protected popupPosition = {
    top: 'inherit',
    left: 'inherit'
  };

  protected isControlPointDetailEdit: boolean = false;
  ponderations: any[] = [];
  selectedPonderationId: number | null = null;
  selectedStandardCode: string | null = null;

  constructor(
    protected controlPointsHttpService: ControlPointsService,
    protected currentStateService: CurrentStateService,
    private fonctionsService: FonctionsService,
    private seismographService: SeismographService,
    private dialog: MatDialog,
    private ponderationService: PonderationService,
    @Inject(MAT_DIALOG_DATA) public data: any, private translateService: TranslateService
  ) {
    // Initialize the form group with controls
    this.form = new FormGroup({
      number: new FormControl(null, [Validators.required]),
      // TO DO : Check for name min length
      name: new FormControl('', Validators.required),
      description: new FormControl('', [Validators.maxLength(500)]),
      device: new FormControl(''),
      x: new FormControl('', [Validators.required]),
      y: new FormControl('', [Validators.required]),
      z: new FormControl(0)
    });    
    this.epsgIndex = this.parseEpsgIndex(epsgIndexData);

    // Prepare the EPSG codes array
    this.epsgCodes = Object.keys(this.epsgIndex).map(code => ({
      id: code,
      text: `${this.epsgIndex[code].name} (${code})`
    }));

    // is trigger when popup is close from outside, for example when change site
    this.currentStateService.closePopup$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (closeMode: PopupCloseMode | null) => {
        if (closeMode != null && closeMode === PopupCloseMode.CLOSE) {
          this.onClose(closeMode);
        }
      }
    });
  }

  updateForm(controlPoint: ControlPoint) {
    this.form!.patchValue({
      number: controlPoint.numeroPoint,
      name: controlPoint.name,
      description: controlPoint.description,
      x: controlPoint.x,
      y: controlPoint.y,
      z: controlPoint.z ?? 0
    });

    if (this.data.device) {
      this.form!.patchValue({
        device: this.data.device
      });
    }
  }

  ngOnInit(): void {
    this.getAllStandards();
    this.form!.get('number')?.setValue(this.lastNumber);
    if (this.data.currentControlPoint) {
      this.isControlPointDetailEdit = true;
      this.updateForm(this.data.currentControlPoint);
      this.setSite(this.data.currentSite);
      
      this.getcurrentPonderation();
    }


    // Initially show no options (empty filtered list)
    this.filteredEpsgCodes = [];

    // Set up typeahead to filter EPSG codes based on user input
    this.typeahead$
      .pipe(
        debounceTime(500), // Wait 300ms after user stops typing
        distinctUntilChanged(), // Only search if the input changes
        tap(() => (this.loading = true)), // Show loading spinner
        switchMap(term => this.searchEpsg(term))
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: results => {
          this.filteredEpsgCodes = [...results]; // Update the filtered list

          setTimeout(() => {
            this.loading = false;
          }, 1000);
        },
        error: () => {
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        }
      });

    const currentCP = this.currentStateService.getCurrentControlPoint();
    if (currentCP) {
      this.form!.patchValue({
        z: currentCP.z.toString() ?? undefined
      });
    }

    (async () => {
      try {
        this.seismographList = await this.seismographService.getAllSeismographs();
        this.seismographList = this.seismographList.filter(x => x.controlPointId == undefined || x.controlPointId == null);
      } catch (err) {
        console.error('Failed to fetch seismographs:', err);
      }
    })();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.dialog.closeAll();
        if (this.typeahead$) {
      this.typeahead$.complete();
    }
  }

  ngOnChanges(changes: SimpleChanges) { }

  //this is position we will send to backed, if user decide to create Control Point,
  //and why there is 'setPositionEpsgSite' and this one ?..
  //because in this one we don't have to 'translate', and there is more accuracy thanks to this
  setPositionEPSG_REFERENCE_OL(customCoordinate: CustomCoordinate) {

    this.position_EPSG_REFERENCE_OL = customCoordinate;
  }

  //this position we show to the user inside popup
  setPositionEpsgSite(customCoordinate: CustomCoordinate) {
    this.positionEpsgSite = customCoordinate;

    this.form!.patchValue({
      x: this.positionEpsgSite.coordX,
      y: this.positionEpsgSite.coordY
    });
  }
  setLastNumber(lastNumber: number) {
    this.lastNumber = lastNumber;
  }

  setSite(site: Site) {
    this.site = site;
    if(site == undefined) {
      return;
    }
    this.searchEpsg(site.epsg)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: result => {
          this.selectedEpsg = result[0];
          if (this.selectedEpsg) {
            this.epsgIsOk = true;
          }
        }
      });
  }

  setIsDegre(isDegre: boolean) {
    if (isDegre) {
      (this.C1 = 'Latitude'), (this.C2 = 'Longitude'), (this.C3 = 'Altitude');
    } else {
      (this.C1 = 'X'), (this.C2 = 'Y'), (this.C3 = 'Z');
    }
  }

  setIsEdit(isEdit: boolean) {
    this.isEdit = isEdit;
    this.popupPosition = {
      top: 'inherit',
      left: '5rem'
    };

    const currentCP = this.currentStateService.getCurrentControlPoint();
    if (!currentCP) {
      console.error('Selected control point is null');
      return;
    }

    this.form!.patchValue({
      number: currentCP.numeroPoint ?? -1,
      name: currentCP.name,
      description: currentCP.description,
      z: currentCP.z.toString() ?? undefined
    });
  }

  onSubmit(): void {
    if (!this.selectedEpsg || this.epsgIsOk === undefined) {
      this.epsgIsOk = false;
    }

    if (!this.form) return;

    if (this.form.valid && this.epsgIsOk === true) {
      let formData = this.form.getRawValue() as MyFormData;

      this.sendControlPoint(formData);
    } else {
      this.form.markAllAsTouched();
    }
  }

  private sendControlPoint(formData: MyFormData) {
    if (!this.site || !this.site.siteId) {
      console.error('Site is not defined or has no siteId.');
      this.onClose(PopupCloseMode.ERROR);
      return;
    }

    const siteId = this.site.siteId;
    const cp = new ControlPoint();
    cp.name = formData.name;
    cp.description = formData.description;
    cp.numeroPoint = formData.number;
    cp.x = this.form.get('x')?.value;
    cp.y = this.form.get('y')?.value;
    cp.z = this.form.get('z')?.value ?? 0;

    cp.siteId = siteId;
    const updatePonderation = (controlPointId: number) => {
      this.ponderationService.updateControlPointPonderation(controlPointId, null, this.selectedStandardCode).subscribe({
            next: () => {
            },
            error: () => {
            }
        });
    };
    if (!this.isEdit) {
      this.controlPointsHttpService
        .postControlPoint(this.site!.siteId!, cp, this.selectedEpsg.id.toString())
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (createdControlPoint: ControlPoint) => {
            this.controlPointsHttpService.addOrUpdateLocalControlPoint(createdControlPoint);
            this.currentStateService.setCurrentControlPoint(createdControlPoint);
            if (this.form!.get('device')?.value) {
              (async () => {
                try {
                  const success = await this.seismographService.createControlPointInstrument(
                    createdControlPoint.controlPointId,
                    (this.form!.get('device').value as Seismograph).id,
                    createdControlPoint.name
                  );

                  if (!success) {
                    console.error('Failed to create control point instrument.');
                  }
                } catch (error) {
                  console.error('Error creating control point instrument:', error);
                }
              })();
            }
            updatePonderation(createdControlPoint.controlPointId);
            this.fonctionsService.launch_toast(true, this.translateService.instant('SUCCESS.CONTROL_POINT_CREATED'));

            this.onClose(PopupCloseMode.CREATED);
          },
          error: () => {
            this.onClose(PopupCloseMode.ERROR);
          }
        });
    } else {
      const currentCP = this.currentStateService.getCurrentControlPoint();
      if (!currentCP || !currentCP.controlPointId) {
        console.error('Unable to edit: Current control point is undefined or has no ID.');
        this.onClose(PopupCloseMode.ERROR);
        return;
      }
      cp.numeroPoint = currentCP.numeroPoint;
      cp.controlPointId = currentCP.controlPointId;
      this.controlPointsHttpService
        .putControlPoint(this.site!.siteId!, cp, this.selectedEpsg.id.toString())
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (createdControlPoint: ControlPoint) => {
            this.controlPointsHttpService.addOrUpdateLocalControlPoint(createdControlPoint);
            this.currentStateService.setCurrentControlPoint(createdControlPoint);
            this.fonctionsService.launch_toast(true, 'Position was modified.');
            this.onClose(PopupCloseMode.EDITED);
            updatePonderation(createdControlPoint.controlPointId);
          },
          error: () => {
            this.onClose(PopupCloseMode.ERROR);
          }
        });
    }
  }

  searchEpsg(term: string) {
    if (term === '' || !term) {
      return of([]); // If the user clears the search, show no results
    }

    // Filter EPSG codes by matching the search term
    const results = this.epsgCodes.filter(code => code.text.toLowerCase().includes(term.toLowerCase()));

    return of(results); // Return filtered results
  }

  initEpsgFromSite(epsg: string): Observable<any[]> {
    return of(epsg).pipe(
      debounceTime(300), // Wait 300ms after the last keystroke
      distinctUntilChanged(), // Only proceed if the term has changed
      switchMap(searchTerm => this.searchEpsg(searchTerm))
    );
  }

  protected compareItems(item1: any, item2: any): boolean {
    return item1 && item2 ? item1 === item2 : item1 === item2;
  }

  protected onClose(closeMode: PopupCloseMode = PopupCloseMode.CLOSE) {
    this.close.emit(closeMode);
    this.dialog.closeAll();
  }

  parseEpsgIndex(data: any): EpsgIndex {
    const parsedIndex: EpsgIndex = {};

    // Iterate through the keys of the data and manually map them
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const item = data[key];

        // Validate and assign default values for missing fields
        parsedIndex[key] = {
          name: item.name || 'Unknown',
          proj4: item.proj4 || null,
          wkt: item.wkt || null,
          bbox: Array.isArray(item.bbox) ? item.bbox : [],
          unit: item.unit || 'Unknown',
          area: item.area || 'Unknown',
          accuracy: typeof item.accuracy === 'number' ? item.accuracy : null
        };
      }
    }

    return parsedIndex;
  }
    getcurrentPonderation() {
    this.ponderationService.getPonderation(0, this.data.currentControlPoint.controlPointId).subscribe({
      next: (response) => {
        this.selectedPonderationId = response.data?.ponderationId || null;
          this.selectedStandardCode = response.data?.standardCode || null;
      }
    });
  }
  getAllStandards() {
    this.ponderationService.getAllStandards().subscribe({
      next: (response) => {
        this.ponderations = (response.data ?? []).map((standard: Standard & { Id?: number; Code?: string; Country?: string }) => ({
          ponderationId: standard.id ?? standard.Id,
          standardCode: standard.code ?? standard.Code ?? null,
          name: `${standard.code ?? standard.Code}${(standard.country ?? standard.Country) ? ' - ' + (standard.country ?? standard.Country) : ''}`
        }));
      }
    });
  }
}
